import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bullseye,
  Content,
  EmptyState,
  EmptyStateBody,
  Label,
  List,
  ListItem,
  Pagination,
  SearchInput,
  Spinner,
  Switch,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { ExpandableRowContent, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { ServerIcon } from '@patternfly/react-icons';
import { ADMIN_GPU_LIST_LIMIT, useAdminGpuServers } from '../../../hooks/useAdminVerdaServers';
import type { VerdaGpuServer } from '../../../types/models/Verda';
import { formatDateTime } from '../../../utils/dateFormat';
import { formatCurrencyAmount, formatUnitPrice } from '../../../utils/money';
import {
  VERDA_GPU_DEFAULT_STATUSES,
  VERDA_GPU_STATUSES,
  isNonPositiveMargin,
  matchesServerSearch,
  namespaceMarker,
  severityColor,
  verdaGpuStatusLabel,
  worstSeverity,
} from './verdaGpuServers';
import { NoSearchResults, StatusMultiSelect, useDebouncedValue } from './VerdaGpuParts';
import { VerdaErrorAlert, VerdaRefreshButton } from './VerdaTabParts';

const STATUS_OPTIONS = VERDA_GPU_STATUSES.map((status) => ({
  value: status,
  label: verdaGpuStatusLabel(status).label,
}));
const COLUMN_COUNT = 13;

interface VerdaServersTabProps {
  /** Defers the fetch until the staff gate has passed. */
  isStaff: boolean;
}

/**
 * Admin › Verda › GPU servers — every client's GPU server, newest first, across installations
 * (`GET /admin/verda/servers`, a database read). Filters: internal statuses (default: every
 * non-terminal one) and "Needs attention" (`attention=1`), both server-side. No row actions: they
 * live on the server detail page. Refreshed manually only (no auto-poll — Bruno, 2026-09-28).
 *
 * List-table decision (Bruno, 2026-09-28): client-side search by hostname, client name or product
 * label, 400 ms debounce, top + bottom pagination at 25 per page. Client-side filtering is correct
 * because the read is bounded by the backend `limit` (the maximum, 500, is requested).
 */
const VerdaServersTab: React.FunctionComponent<VerdaServersTabProps> = ({ isStaff }) => {
  const [statuses, setStatuses] = useState<string[]>(VERDA_GPU_DEFAULT_STATUSES);
  const [attentionOnly, setAttentionOnly] = useState(false);
  const filters = useMemo(() => ({ statuses, attentionOnly }), [statuses, attentionOnly]);
  const { servers, namespace, loading, error, refetch } = useAdminGpuServers(filters, isStaff);

  const [searchValue, setSearchValue] = useState('');
  const debouncedSearch = useDebouncedValue(searchValue);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(() => new Set());

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statuses, attentionOnly]);

  const filtered = useMemo(() => {
    const query = debouncedSearch.trim().toLowerCase();
    return (servers ?? []).filter((server) => matchesServerSearch(server, query));
  }, [servers, debouncedSearch]);

  const paginated = useMemo(() => filtered.slice((page - 1) * perPage, page * perPage), [filtered, page, perPage]);

  // Keep the query string stable whatever the click order, so re-selecting doesn't refetch.
  const selectStatuses = (next: string[]) => setStatuses(VERDA_GPU_STATUSES.filter((status) => next.includes(status)));

  const toggleExpanded = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPage: number) => setPage(newPage);
  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const renderRow = (server: VerdaGpuServer, rowIndex: number) => {
    const status = verdaGpuStatusLabel(server.status);
    const worst = worstSeverity(server.attention);
    const marker = namespaceMarker(server.namespace, namespace);
    const hasAttention = server.attention.length > 0;
    const isExpanded = hasAttention && expanded.has(server.id);
    return (
      <Tbody key={server.id} isExpanded={isExpanded}>
        <Tr isContentExpanded={isExpanded}>
          <Td
            expand={
              hasAttention
                ? { rowIndex, isExpanded, onToggle: () => toggleExpanded(server.id), expandId: 'verda-gpu-server' }
                : undefined
            }
          />
          <Td dataLabel="ID">{server.id}</Td>
          <Td dataLabel="Hostname">
            <Link to={`/admin/verda/servers/${server.id}`}>
              <span className="pf-v6-u-font-family-monospace">{server.hostname}</span>
            </Link>
          </Td>
          <Td dataLabel="Client">{server.client.name ?? `#${server.client.id}`}</Td>
          <Td dataLabel="Product">
            {server.product?.label ?? '—'}
            <div>
              <span className="pf-v6-u-font-family-monospace pf-v6-u-font-size-sm">{server.instance_type}</span>
            </div>
          </Td>
          <Td dataLabel="Status">
            <Label color={status.color} isCompact>
              {status.label}
            </Label>
            <div>
              <Content component="small" className="pf-v6-u-text-color-subtle">
                tenant: {server.tenant_status}
              </Content>
            </div>
          </Td>
          <Td dataLabel="Location">{server.location_code}</Td>
          <Td dataLabel="IP">{server.ip ?? '—'}</Td>
          <Td dataLabel="Sale / h HT" modifier="nowrap">
            {formatUnitPrice(server.price.sale_ht_per_hour)}
          </Td>
          <Td dataLabel="Margin / h" modifier="nowrap">
            <span
              className={
                isNonPositiveMargin(server.price.margin_ht_per_hour) ? 'pf-v6-u-text-color-status-danger' : undefined
              }
            >
              {formatCurrencyAmount(server.price.margin_ht_per_hour, 'EUR')}
            </span>
          </Td>
          <Td dataLabel="Paid through">{formatDateTime(server.paid_through)}</Td>
          <Td dataLabel="Attention">
            {worst === null ? (
              '—'
            ) : (
              <Label color={severityColor(worst)} isCompact>
                {server.attention.length} {worst === 'CRITICAL' ? 'critical' : 'warning'}
              </Label>
            )}
          </Td>
          <Td dataLabel="Installation">
            {marker === null ? (
              '—'
            ) : (
              <Label color="orange" isCompact>
                {marker}
              </Label>
            )}
          </Td>
        </Tr>
        {hasAttention && (
          <Tr isExpanded={isExpanded}>
            <Td />
            <Td dataLabel="Attention" colSpan={COLUMN_COUNT - 1}>
              <ExpandableRowContent>
                <List isPlain aria-label={`Attention items of ${server.hostname}`}>
                  {server.attention.map((item) => (
                    <ListItem key={item.code}>
                      <Label color={severityColor(item.severity)} isCompact>
                        {item.severity}
                      </Label>{' '}
                      <span className="pf-v6-u-font-family-monospace">{item.code}</span> — {item.message}
                    </ListItem>
                  ))}
                </List>
              </ExpandableRowContent>
            </Td>
          </Tr>
        )}
      </Tbody>
    );
  };

  const renderBody = () => {
    if (error) {
      return <VerdaErrorAlert what="GPU servers" message={error} />;
    }
    if (servers === null) {
      return (
        <Bullseye>
          <Spinner size="lg" aria-label="Loading the GPU servers" />
        </Bullseye>
      );
    }
    if (servers.length === 0) {
      return (
        <EmptyState titleText="No GPU servers" icon={ServerIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>No GPU server matches the status and attention filters.</EmptyStateBody>
        </EmptyState>
      );
    }
    if (filtered.length === 0) {
      return <NoSearchResults what="GPU servers" onClear={() => setSearchValue('')} />;
    }
    return (
      <>
        <Table aria-label="GPU servers" variant="compact" isExpandable>
          <Thead>
            <Tr>
              <Th screenReaderText="Row expansion" />
              <Th>ID</Th>
              <Th>Hostname</Th>
              <Th>Client</Th>
              <Th>Product</Th>
              <Th>Status</Th>
              <Th>Location</Th>
              <Th>IP</Th>
              <Th>Sale / h HT</Th>
              <Th>Margin / h</Th>
              <Th>Paid through</Th>
              <Th>Attention</Th>
              <Th>Installation</Th>
            </Tr>
          </Thead>
          {paginated.map(renderRow)}
        </Table>
        <Pagination
          itemCount={filtered.length}
          perPage={perPage}
          page={page}
          onSetPage={onSetPage}
          onPerPageSelect={onPerPageSelect}
          variant="bottom"
        />
      </>
    );
  };

  return (
    <>
      <Toolbar>
        <ToolbarContent>
          <ToolbarItem>
            <SearchInput
              placeholder="Search by hostname, client or product..."
              value={searchValue}
              onChange={(_event, value) => setSearchValue(value)}
              onClear={() => setSearchValue('')}
            />
          </ToolbarItem>
          <ToolbarItem>
            <StatusMultiSelect
              options={STATUS_OPTIONS}
              selected={statuses}
              onChange={selectStatuses}
              ariaLabel="Filter by status"
              label="Status"
            />
          </ToolbarItem>
          <ToolbarItem alignSelf="center">
            <Switch
              id="verda-gpu-servers-attention"
              label="Needs attention"
              isChecked={attentionOnly}
              onChange={(_event, checked) => setAttentionOnly(checked)}
            />
          </ToolbarItem>
          <ToolbarItem>
            <VerdaRefreshButton loading={loading} onRefresh={refetch} what="GPU servers" />
          </ToolbarItem>
          {servers !== null && !error && (
            <ToolbarItem alignSelf="center">
              <span className="pf-v6-u-font-size-sm pf-v6-u-text-color-subtle">
                {filtered.length} shown
                {servers.length >= ADMIN_GPU_LIST_LIMIT && ` (the newest ${ADMIN_GPU_LIST_LIMIT} only)`}
              </span>
            </ToolbarItem>
          )}
          {servers !== null && !error && filtered.length > 0 && (
            <ToolbarItem variant="pagination">
              <Pagination
                itemCount={filtered.length}
                perPage={perPage}
                page={page}
                onSetPage={onSetPage}
                onPerPageSelect={onPerPageSelect}
                isCompact
              />
            </ToolbarItem>
          )}
        </ToolbarContent>
      </Toolbar>
      {renderBody()}
    </>
  );
};

export default VerdaServersTab;
